import { create } from "zustand";
import {
  INotificationBaseRelatedToShow,
  IPackage,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";
import {
  createRequestSeq,
  languageCodeQuery,
  resolveCacheLanguage,
} from "store/utils/localised-cache.utils";

type State = {
  items: Record<string, Array<IPackage>>;
  language: Record<string, string>;
  isLoading: boolean;
};

type Action = {
  fetch: (
    showId: string,
    force?: boolean,
    languageCode?: string
  ) => Promise<void>;
  update: (showId: string, languageCode?: string) => Promise<void>;
  autoCatch: (showId: string) => Promise<void>;
  getByShowId: (showId: string) => Array<IPackage>;
};

const nextRequest = createRequestSeq();

export const usePackages = create(
  devtools<State & Action>((set, get) => ({
    items: {},
    language: {},
    isLoading: false,
    showId: null,

    getByShowId: (showId: string): Array<IPackage> => {
      const { items } = get();

      if (!items?.[showId]) {
        return [];
      }

      return items[showId];
    },

    autoCatch: async (showId: string) => {
      const { update } = get();

      IdleManager.getInstance().addSubscriber(`package-${showId}`, async () => {
        await update(showId);
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_PACKAGE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_PACKAGE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETE_PACKAGE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });
    },

    update: async (showId: string, languageCode?: string) => {
      const { items, language } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);
      const isLatestRequest = nextRequest(showId);

      try {
        const packages = await apiV2.package.getAll({
          showId,
          ...languageCodeQuery(nextLanguage),
        });

        if (!isLatestRequest()) {
          return;
        }

        set(() => ({
          items: {
            ...items,

            [showId]: packages,
          },
          language: { ...language, [showId]: nextLanguage },
        }));
      } catch (e) {
        console.error("package store:", e);
      }
    },
    fetch: async (showId: string, force = false, languageCode?: string) => {
      const { isLoading, items, language, update } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);

      if (!force && language[showId] === nextLanguage) {
        if (items[showId] || isLoading) {
          return;
        }
      }

      set(() => ({ isLoading: true }));

      try {
        await update(showId, nextLanguage);
      } catch (e) {
        console.error(e);
      } finally {
        set(() => ({ isLoading: false, showId }));
      }
    },
  }))
);

registerStore(usePackages);
